import { KeenIcon, LazyFormikSelect, CustomOption, CustomSingleValue, DatePicker, FormikStateSelect } from '@/components';
import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import * as Yup from "yup";
import { Formik, Form, Field, ErrorMessage, FieldArray, useFormikContext } from "formik";
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import tagsService from '@/services/setting/tagsService';
import customFieldService from '@/services/setting/customFieldService';
import contactService from '@/services/contact/contactService';
import contactTypeService from '@/services/contact/contactTypeService';
import userService from '@/services/setting/userService';
import CreatableSelect from 'react-select/creatable';
import { CustomFieldRadioGroup } from '@/components/form/CustomFieldRadioGroup';
import Select from 'react-select';
import { toast } from 'sonner';
import { CustomFieldModal } from '../custom-fields/CustomFieldModal';
import { setIn } from 'formik';
import { ContactTypeModal } from '@/pages/contact';
import { useLazyOptions, useUserOptions, useContactTypeOptions, useTagOptions, useSpecialityOptions } from '@/hooks';
import { useAbility } from '@/providers/';
import { Can } from '@casl/react';
import { InputSanitizers, MapIdsToLabels, SelectHelper, stateCityMap, urlSchema } from '@/utils'
import { MaskedInput, FormikUnsavedChangesGuard } from '@/components/form';
import templateService from '@/services/billing/invoice/invoiceTemplateService';
import { components } from 'react-select';
import { getGlobalSelectClass } from '@/utils/GlobalSelectClass';


/**
 * The phone types offered on the contact form. A fixed list keeps "Office",
 * "office" and "Off." from all ending up in the same column.
 */
const PHONE_TYPES = ['Mobile', 'Home', 'Office', 'Fax', 'Other'];

/**
 * Contacts saved before this was a fixed list, and contacts brought in by an
 * import, can hold anything. Keep whatever is already stored as an option so
 * opening a contact for editing shows its real value and saving it back does
 * not quietly change it.
 */
const phoneTypeOptions = (currentValue) => {
  const options = PHONE_TYPES.map((type) => ({ label: type, value: type }));

  if (currentValue && !PHONE_TYPES.includes(currentValue)) {
    options.push({ label: currentValue, value: currentValue });
  }

  return options;
};

const isCheckedSingleCheckboxValue = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return Boolean(value.checked);
  }
  if (value === true || value === 1 || value === '1') return true;
  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase();
    return normalizedValue === '1' || normalizedValue === 'true';
  }
  return false;
};

const getSingleCheckboxOtherValue = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return typeof value.other === 'string' ? value.other.trim() : '';
  }

  if (typeof value === 'string') {
    return value.trim().length > 0 && value !== '0' && value.toLowerCase() !== 'false'
      ? value.trim()
      : '';
  }

  return '';
};

const hasSingleCheckboxSelection = (value) => {
  return isCheckedSingleCheckboxValue(value) || Boolean(getSingleCheckboxOtherValue(value));
};

const AddContacts = () => {
  const [loadingTags, setLoadingTags] = useState(false)
  const [loadingFields, setLoadingFields] = useState(false)
  const [availableTags, setAvailableTags] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [dataLoading, setDataLoading] = useState(false)
  const [contactData, setContactData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false);
  const [createContactLoading, setCreateContactLoading] = useState(false)
  const [updateFieldloading, setUpdateFieldloading] = useState(false)
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('create');
  const [selectedData, setSelectedData] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [loading, setLoading] = useState(false)
  const { id } = useParams();
  const contactId = id;
  const Navigate = useNavigate()
  const { ability } = useAbility();
  const [templateData, setTemplateData] = useState([])
  const [visibleCustomFields, setVisibleCustomFields] = useState([]);
  const formikRef = useRef(null);
  const modalFormikRef = useRef(null);
  const [addFieldSelect, setAddFieldSelect] = useState(null);
  const location = useLocation();
  const { from, contact_id, matterID } = location.state || {};
  const loadContactTypeOptions = useContactTypeOptions({ defaultPageSize: 20 });
  const loadUserOptions = useUserOptions({ defaultPageSize: 20 });
  const loadTagOptions = useTagOptions({ defaultPageSize: 20, type: 'contact', });
  const loadSpecialityOptions = useSpecialityOptions({ defaultPageSize: 20 });
  const extraCheckboxOptionsById = useMemo(() => {
    const extrasById = {};
    if (!contactData?.custom_fields?.length || !customFields?.length) {
      return extrasById;
    }

    const fieldMap = new Map(customFields.map(field => [field.id, field]));
    contactData.custom_fields.forEach((customField) => {
      const field = fieldMap.get(customField.id);
      if (!field || field.field_type !== 'checkbox' || field.is_multi_select !== 1) {
        return;
      }

      let values = [];
      const pivotValue = customField?.pivot?.value;
      if (Array.isArray(pivotValue)) {
        values = pivotValue;
      } else if (typeof pivotValue === 'string' && pivotValue.length) {
        try {
          const parsed = JSON.parse(pivotValue);
          values = Array.isArray(parsed) ? parsed : [pivotValue];
        } catch {
          values = pivotValue.split(',').map(v => v.trim()).filter(Boolean);
        }
      } else if (pivotValue != null) {
        values = [String(pivotValue)];
      }

      let optionsList = [];
      if (field.options) {
        try {
          optionsList = Array.isArray(field.options)
            ? field.options
            : JSON.parse(field.options.replace(/&quot;/g, '"'));
        } catch {
          optionsList = [];
        }
      }

      const normalizedOptions = optionsList.map(value => String(value).toLowerCase());
      const seen = new Set();
      const extras = [];
      values.forEach((value) => {
        const normalizedValue = String(value).toLowerCase();
        if (!normalizedValue || normalizedOptions.includes(normalizedValue) || seen.has(normalizedValue)) {
          return;
        }
        extras.push(String(value));
        seen.add(normalizedValue);
      });

      if (extras.length) {
        extrasById[field.id] = extras;
      }
    });

    return extrasById;
  }, [contactData, customFields]);
  const extraSingleCheckboxOptionsById = useMemo(() => {
    const extrasById = {};
    if (!contactData?.custom_fields?.length || !customFields?.length) {
      return extrasById;
    }

    const fieldMap = new Map(customFields.map(field => [field.id, field]));
    contactData.custom_fields.forEach((customField) => {
      const field = fieldMap.get(customField.id);
      if (!field || field.field_type !== 'checkbox' || field.is_multi_select !== 0) {
        return;
      }

      const pivotValue = customField?.pivot?.value;
      if (typeof pivotValue !== 'string' || !pivotValue.trim()) {
        return;
      }

      try {
        const parsed = JSON.parse(pivotValue);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && typeof parsed.other === 'string' && parsed.other.trim()) {
          extrasById[field.id] = parsed.other.trim();
        }
      } catch {
        const normalizedValue = pivotValue.trim().toLowerCase();
        if (normalizedValue !== '0' && normalizedValue !== '1' && normalizedValue !== 'true' && normalizedValue !== 'false') {
          extrasById[field.id] = pivotValue.trim();
        }
      }
    });

    return extrasById;
  }, [contactData, customFields]);

  const getValidationSchema = (customFields) => {
    const customFieldShape = {};
    const phoneRegex = /^\+\d{10,15}$/; // Global/International
    const usPhoneRegex = /^\d{10}$/; // United States

    customFields.forEach((field) => {

      if (field.type === 'contact') {
        const isRequired = field.is_required;
        const isVisible =
          field.is_show === 1 ||
          visibleCustomFields.includes(field.id); // ✅ field is shown in form

        if (field.is_multi_select === 1) {
          if (isRequired && isVisible) {
            customFieldShape[field.id] = Yup.array()
              .transform((value, originalValue) => {
                // 👇 convert '' or null to []
                if (!originalValue) return [];
                return Array.isArray(originalValue) ? originalValue : [originalValue];
              })
              .of(Yup.string())
              .min(1, `${field.title} is required`);
          } else {
            customFieldShape[field.id] = Yup.array()
              .transform((value, originalValue) => {
                if (!originalValue) return [];
                return Array.isArray(originalValue) ? originalValue : [originalValue];
              })
              .of(Yup.string())
              .nullable();
          }
        } else {
          const isSingleCheckbox = field.field_type === 'checkbox';

          if (isSingleCheckbox) {
            if (isRequired && isVisible) {
              customFieldShape[field.id] = Yup.mixed().test(
                'single-checkbox-required',
                `${field.title} is required`,
                (value) => hasSingleCheckboxSelection(value)
              );
            } else {
              customFieldShape[field.id] = Yup.mixed().nullable();
            }
          } else if (isRequired && isVisible) {
            customFieldShape[field.id] = Yup.string()
              .required(`${field.title} is required`);
          } else {
            customFieldShape[field.id] = Yup.string().nullable();
          }
        }
      }
    });

    const mobileNumbersSchema = Yup.array().of(
      Yup.object().shape({
        title: Yup.string()
          // .required('Title is required')
          .test(
            'unique-title',
            'This title is duplicated.',
            function (value) {
              const { path, options } = this;
              const { mobile_numbers } = options.context || {};
              const currentIndex = parseInt(path.match(/\d+/)?.[0] || '-1');
              if (!value || !mobile_numbers) return true;

              // Find the first index with this value
              const firstIndex = mobile_numbers.findIndex(item => item?.title === value);

              // Only return false if this is a duplicate AND not the first one
              return currentIndex === firstIndex;
            }
          ),
        number: Yup.string()
          // .required('Number is required')
          .transform(value => (value ? value.replace(/\D/g, '') : value))
          .matches(usPhoneRegex, 'Must be a valid 10-digit US number')
          .test(
            'unique-number',
            'This number is duplicated.',
            function (value) {
              const { path, options } = this;
              const { mobile_numbers } = options.context || {};
              const currentIndex = parseInt(path.match(/\d+/)?.[0] || '-1');
              if (!value || !mobile_numbers) return true;

              // Find the first index with this value
              const cleanedValue = value.replace(/\D/g, '');
              const firstIndex = mobile_numbers.findIndex(
                item => item?.number?.replace(/\D/g, '') === cleanedValue
              );

              return currentIndex === firstIndex;
            }
          ),
      })
    ).min(1, 'At least one mobile number is required');

    return Yup.object().shape({
      // business_name: Yup.string().required('Business name is required'),
      first_name: Yup.string().required('First name is required'),
      middle_name: Yup.string(),
      last_name: Yup.string().required('Last name is required'),
      office_number: Yup.string()
        .nullable()
        .notRequired()
        .transform(value => {
          // Strip formatting: (123) 456-7890 => 1234567890
          return value ? value.replace(/\D/g, '') : value;
        })
        .matches(usPhoneRegex, 'Must be a valid 10-digit US number')
        .test('unique-office', 'Office number must be unique', function (value) {
          const { home_number, fax_number } = this.parent;
          return (
            !value || (value !== home_number && value !== fax_number)
          );
        }),

      home_number: Yup.string()
        .nullable()
        .notRequired()
        .transform(value => (value ? value.replace(/\D/g, '') : value))
        .matches(usPhoneRegex, 'Must be a valid 10-digit US number')
        .test('unique-home', 'Home number must be unique', function (value) {
          const { office_number, fax_number } = this.parent;
          return (
            !value || (value !== office_number && value !== fax_number)
          );
        }),

      fax_number: Yup.string()
        .nullable()
        .notRequired()
        .transform(value => (value ? value.replace(/\D/g, '') : value))
        .matches(usPhoneRegex, 'Must be a valid 10-digit US number')
        .test('unique-fax', 'Fax number must be unique', function (value) {
          const { office_number, home_number } = this.parent;
          return (
            !value || (value !== office_number && value !== home_number)
          );
        }),
      email: Yup.string().email('Invalid email'), //.required('Email is required')
      add_type: Yup.string(),
      // add_type: Yup.string().required('Address Type is required'),
      address: Yup.string(),
      // address: Yup.string().required('Address is required'),
      city: Yup.string(),
      // state: Yup.string().required('State is required'),
      state: Yup.string(),
      zip: Yup.string(),
      // zip: Yup.string().required('ZIP is required'),
      assigned_to: Yup.array()
        .of(
          Yup.string().required('User ID is required')
        ),
      // .min(1, 'At least one user must be assigned')
      // .required('Assigned To is required'),
      contact_type_id: Yup.string()
        .required('Contact Type is required'),
      invoice_template_id: Yup.string()
        .required('Invoice Template is required'),
      website: urlSchema('Invalid URL'),
      note: Yup.string(),
      custom_fields: Yup.object().shape(customFieldShape),
      mobile_numbers: mobileNumbersSchema,
      speciality: Yup.string()
        .nullable()
        .test(
          'speciality-required',
          'Specialty is required when contact type is provider',
          function (value) {
            const { contact_type } = this.parent;

            // Get label in a safe way
            let label = '';
            if (typeof contact_type === 'string') {
              label = contact_type;
            } else if (contact_type && typeof contact_type === 'object') {
              label = contact_type.label || '';
            }

            if (label.trim().toLowerCase() === 'provider') {
              return !!value?.trim();
            }

            return true;
          }
        ),

    });
  };

  const getAllCustomFields = async () => {
    setLoadingFields(true)
    try {
      const { data, status } = await customFieldService.getAllCustomFields();
      if (status) {
        // const Data = sortData(data, { mode: "reverse" });
        setCustomFields(data)
      }
    } catch (error) {
      console.error('Error', error)
      toast.error('Failed to fetch Custom Fields.', {
        description: 'An error occurred while fetching Custom Fields. Please try again later.',

      });
    } finally {
      setLoadingFields(false)
    }
  }


  const renderCustomField = (field, formik, onRemove) => {
    const { id, type, title, field_type, placeholder, options, is_show, is_required, is_multi_select } = field;
    if (type !== 'contact') return null;

    const key = `custom_fields.${id}`;
    let parsedOptions = [];

    // Parse options for dropdown or multi-checkbox fields
    if (['dropdown', 'checkbox', 'radio'].includes(field_type)) {
      try {
        parsedOptions = Array.isArray(options)
          ? options
          : JSON.parse(options.replace(/&quot;/g, '"')); // Parse JSON options if it's a string
        console.log(`Parsed Options for ${title}:`, parsedOptions);
      } catch (err) {
        console.error(`Failed to parse options for field "${title}":`, err);
      }
    }

    // Ensure the value for multi-checkbox is properly parsed as an array
    let selectedValues = formik.values?.custom_fields?.[id] || [];
    if (typeof selectedValues === 'string') {
      try {
        selectedValues = JSON.parse(selectedValues); // Try to parse it as an array
      } catch (err) {
        selectedValues = []; // Fallback to an empty array if parsing fails
      }
    }

    const normalizedSelectedValues = Array.isArray(selectedValues)
      ? selectedValues.map(value => String(value).toLowerCase())
      : [];
    const extraOptions = extraCheckboxOptionsById[id] || [];
    const checkboxOptions = [
      ...parsedOptions,
      ...extraOptions,
    ];
    const singleCheckboxOtherValue =
      field_type === 'checkbox' && is_multi_select === 0
        ? getSingleCheckboxOtherValue(formik.values?.custom_fields?.[id])
        : '';
    const persistedSingleCheckboxOtherValue =
      field_type === 'checkbox' && is_multi_select === 0
        ? extraSingleCheckboxOptionsById[id] || ''
        : '';
    const displayedSingleCheckboxOtherValue = singleCheckboxOtherValue || persistedSingleCheckboxOtherValue;
    const shouldShowFieldLabel = !(field_type === 'checkbox' && is_multi_select === 0);

    return (
      <div key={`${id}-${is_show}`} className="flex items-start justify-between gap-2">
        <div className="w-full">
          {shouldShowFieldLabel ? (
            <label className="mb-1 form-label">{title} {is_required ? <span className="pl-px text-red-600">*</span> : null}</label>
          ) : null}

          {/* Text Field */}
          {field_type === 'text' && (
            <Field name={key} placeholder={placeholder} className="input" />
          )}

          {/* Date Field */}
          {field_type === 'date' && (
            <Field name={key} component={DatePicker} isRange={false} />
          )}

          {/* Dropdown Field */}
          {field_type === 'dropdown' && (
            <CreatableSelect
              isMulti={is_multi_select === 1}
              options={parsedOptions.map(opt => ({ label: opt.toLowerCase(), value: opt.toLowerCase() }))}
              value={
                is_multi_select === 1
                  ? parsedOptions
                    .map(opt => ({ label: opt.toLowerCase(), value: opt.toLowerCase() }))
                    .filter(opt =>
                      Array.isArray(formik.values?.custom_fields?.[id]) &&
                      formik.values.custom_fields[id].includes(opt.value)
                    )
                  : parsedOptions
                    .map(opt => ({ label: opt.toLowerCase(), value: opt.toLowerCase() }))
                    .find(opt => opt.value === formik.values?.custom_fields?.[id])
              }
              onChange={(val) => {
                if (!val) {
                  formik.setFieldValue(key, field.is_multi_select === 1 ? [] : '');
                  return;
                }

                if (field.is_multi_select === 1) {
                  formik.setFieldValue(
                    key,
                    Array.isArray(val) ? val.map(v => v.value) : []
                  );
                } else {
                  formik.setFieldValue(key, val.value);
                }
              }}
              placeholder={placeholder}
              classNames={getGlobalSelectClass()}
              className="p-0 input react-select-container focus-within:border-none"
              classNamePrefix="react-select"
            />
          )}

          {/* Single Checkbox Field */}
          {field_type === 'checkbox' && is_multi_select === 0 && (
            <Field name={key}>
              {({ field, form }) => (
                <div className="space-y-2">
                  <div className="flex items-center mb-2">
                    <input
                      type="checkbox"
                      id={`${key}-single`}
                      className="mr-2 checkbox checkbox-sm"
                      checked={isCheckedSingleCheckboxValue(field.value)}
                      onChange={() => {
                        const nextChecked = !isCheckedSingleCheckboxValue(field.value);
                        const otherValue = getSingleCheckboxOtherValue(field.value);

                        form.setFieldValue(
                          key,
                          otherValue
                            ? { checked: nextChecked, other: otherValue }
                            : nextChecked
                        );
                      }}
                    />
                    <label htmlFor={`${key}-single`} className="form-label !inline-flex cursor-pointer !mb-0">
                      {title} {is_required ? <span className="pl-px text-red-600">*</span> : null}
                    </label>
                  </div>
                  {displayedSingleCheckboxOtherValue ? (
                    <div className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        id={`${key}-other`}
                        className="mr-2 checkbox checkbox-sm"
                        checked={Boolean(singleCheckboxOtherValue)}
                        onChange={() => {
                          const isMainChecked = isCheckedSingleCheckboxValue(field.value);
                          if (singleCheckboxOtherValue) {
                            form.setFieldValue(key, isMainChecked);
                            return;
                          }

                          form.setFieldValue(key, {
                            checked: isMainChecked,
                            other: displayedSingleCheckboxOtherValue,
                          });
                        }}
                      />
                      <label htmlFor={`${key}-other`} className="form-label !inline-flex cursor-pointer !mb-0">
                        {displayedSingleCheckboxOtherValue}
                      </label>
                    </div>
                  ) : null}
                </div>
              )}
            </Field>
          )}

          {/* Radio buttons */}
          {field_type === 'radio' && parsedOptions.length > 0 && (
            <CustomFieldRadioGroup
              name={key}
              options={parsedOptions}
              value={formik.values?.custom_fields?.[id]}
              onChange={(opt) => formik.setFieldValue(key, opt)}
            />
          )}

          {/* Multi-checkbox */}
          {field_type === 'checkbox' && is_multi_select === 1 && checkboxOptions.length > 0 && (
            <div>
              {checkboxOptions.map((opt, index) => (
                <div key={index} className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    id={`${key}-${opt.toLowerCase()}`}
                    name={key}
                    value={opt.toLowerCase()}
                    checked={normalizedSelectedValues.includes(opt.toLowerCase())} // Check if it's in the selected array
                    onChange={() => {
                      const currentValues = normalizedSelectedValues || [];
                      if (currentValues.includes(opt.toLowerCase())) {
                        formik.setFieldValue(key, currentValues.filter(v => v !== opt.toLowerCase()));
                      } else {
                        formik.setFieldValue(key, [...currentValues, opt.toLowerCase()]);
                      }
                    }}
                    className="mr-2 checkbox checkbox-sm"
                  />
                  <label htmlFor={`${key}-${opt.toLowerCase()}`} className="form-label !inline-flex cursor-pointer">
                    {opt}
                  </label>
                </div>
              ))}
            </div>
          )}

          {/* Error Message */}
          {formik.errors?.custom_fields?.[id] && (
            <div className="text-red-500 text-[11px] mt-1">
              {formik.errors?.custom_fields[id]}
            </div>
          )}
        </div>

        {/* Remove Button */}
        {(field.is_show === 0 || !customFields.find(f => f.id === field.id)?.is_show) && (
          <div>
            <label className='mb-1 text-transparent bg-transparent select-none form-label'>.</label>
            <button type="button" onClick={() => onRemove(id)} className="h-[40px] flex items-center justify-center btn btn-sm btn-danger btn-outline">
              <KeenIcon icon="trash" />
            </button>
          </div>
        )}
      </div>
    );
  };





  // const renderCustomField = (field, formik, onRemove) => {
  //   const { id, type, title, field_type, placeholder, options, is_show, is_required, is_multi_select } = field;
  //   if (type !== 'contact') return null;
  //   console.log(formik.errors)
  //   const key = `custom_fields.${id}`;
  //   let parsedOptions = [];

  //   if (field_type === 'dropdown') {
  //     try {
  //       parsedOptions = Array.isArray(options)
  //         ? options
  //         : JSON.parse(options.replace(/&quot;/g, '"'));
  //       console.log(parsedOptions)
  //     } catch (err) {
  //       console.error(`Failed to parse options for field "${title}":`, err);
  //     }
  //   }
  //   console.log('Fields ------->', field)
  //   return (
  //     <div key={`${id}-${is_show}`} className="flex items-start justify-between gap-2">
  //       <div className="w-full">
  //         <label className="mb-1 form-label">{title} {is_required ? <span className="pl-px text-red-600">*</span> : null}</label>

  //         {field_type === 'text' && (
  //           <Field name={key} placeholder={placeholder} className="input" />
  //         )}

  //         {field_type === 'date' && (
  //           <Field name={key} type="date" className="input" />
  //         )}

  //         {field_type === 'dropdown' && (
  //           <CreatableSelect
  //             isMulti={is_multi_select === 1}
  //             options={parsedOptions.map(opt => ({ label: opt.toLowerCase(), value: opt.toLowerCase() }))}
  //             value={
  //               is_multi_select === 1
  //                 ? parsedOptions
  //                   .map(opt => ({ label: opt.toLowerCase(), value: opt.toLowerCase() }))
  //                   .filter(opt =>
  //                     Array.isArray(formik.values?.custom_fields?.[id]) &&
  //                     formik.values.custom_fields[id].includes(opt.value)
  //                   )
  //                 : parsedOptions
  //                   .map(opt => ({ label: opt.toLowerCase(), value: opt.toLowerCase() }))
  //                   .find(opt => opt.value === formik.values?.custom_fields?.[id])
  //             }
  //             onChange={(val) => {
  //               if (!val) {
  //                 formik.setFieldValue(key, field.is_multi_select === 1 ? [] : '');
  //                 return;
  //               }

  //               if (field.is_multi_select === 1) {
  //                 formik.setFieldValue(
  //                   key,
  //                   Array.isArray(val) ? val.map(v => v.value) : []
  //                 );
  //               } else {
  //                 formik.setFieldValue(key, val.value);
  //               }
  //             }}
  //             placeholder={placeholder}
  //             classNames={getGlobalSelectClass()}
  //             className="p-0 input react-select-container focus-within:border-none"
  //             classNamePrefix="react-select"
  //           />
  //         )}

  //         {field_type === 'checkbox' && (
  //           console.log('!!field.value', field),
  //           <Field name={key}>
  //             {({ field, form }) => (
  //               <label className="switch w-fit h-[40px]">
  //                 <input
  //                   type="checkbox"
  //                   checked={!!field.value}
  //                   onChange={() => form.setFieldValue(key, !field.value)}
  //                 />
  //               </label>
  //             )}
  //           </Field>
  //         )}

  //         {formik.errors?.custom_fields?.[id] && (
  //           <div className="text-red-500 text-[11px] mt-1">
  //             {formik.errors?.custom_fields[id]}
  //           </div>
  //         )}
  //         {/* <ErrorMessage name={key} component="div" className="text-red-500 text-[11px] mt-1" /> */}
  //       </div>
  //       {(field.is_show === 0 || !customFields.find(f => f.id === field.id)?.is_show) && (
  //         <div>
  //           <label className='mb-1 text-transparent bg-transparent select-none form-label'>.</label>
  //           <button type="button" onClick={() => onRemove(id)} className="h-[40px] flex items-center justify-center btn btn-sm btn-danger btn-outline">
  //             <KeenIcon icon="trash" />
  //           </button>
  //         </div>
  //       )}
  //     </div>
  //   );
  // };

  const humanizeValidationKey = (key) => {
    const phoneMatch = key.match(/^phone_number\.(\d+)\.(\w+)$/);
    if (phoneMatch) {
      const index = Number(phoneMatch[1]) + 1;
      const field = phoneMatch[2] === 'number' ? 'number' : 'title';
      return `Mobile Number ${index} ${field}`;
    }

    return key.replace(/\./g, ' ').replace(/_/g, ' ');
  };

  const formatValidationMessage = (message, key) => {
    if (!message) return 'Invalid value.';

    let formatted = String(message);

    formatted = formatted.replace(/phone_number\.(\d+)\.(\w+)/g, (_, rawIndex, field) => {
      const index = Number(rawIndex) + 1;
      const normalizedField = field === 'number' ? 'number' : 'title';
      return `Mobile Number ${index} ${normalizedField}`;
    });

    if (key) {
      const prettyKey = humanizeValidationKey(key);
      formatted = formatted.replace(new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), prettyKey);
    }

    return formatted.replace(/^The\s+/i, '').replace(/\s+field\s+/i, ' ').replace(/^\w/, (c) => c.toUpperCase());
  };

  const buildValidationErrorPayload = (responseErrors = {}) => {
    const formattedErrors = {};
    const friendlyMessages = [];

    Object.entries(responseErrors).forEach(([key, value]) => {
      const rawMessage = Array.isArray(value) ? value[0] : String(value);
      const cleanedMessage = formatValidationMessage(rawMessage, key);
      const phoneMatch = key.match(/^phone_number\.(\d+)\.(\w+)$/);

      if (phoneMatch) {
        const [, index, field] = phoneMatch;

        if (!formattedErrors.mobile_numbers) {
          formattedErrors.mobile_numbers = [];
        }

        formattedErrors.mobile_numbers[Number(index)] = {
          ...(formattedErrors.mobile_numbers[Number(index)] || {}),
          [field]: cleanedMessage,
        };
      } else {
        formattedErrors[key] = cleanedMessage;
      }

      friendlyMessages.push(cleanedMessage);
    });

    return {
      formattedErrors,
      friendlyMessages,
    };
  };

  const handleSubmit = async (formData, resetForm, setErrors) => {
    // return console.log('Submitted:', formData)
    if (contactId) {
      setCreateContactLoading(true)
      try {
        const { data, status } = await contactService.updateContact(formData, contactId);
        if (status) {
          // resetForm()
          toast.success('Contact updated Successfully.', {
            description: `${data.first_name + ' ' + data.last_name} Contact Updated Successfully.`,
            action: {
              label: 'Ok',
            },
          });
          Navigate(from || '/contacts')
        }
      } catch (error) {
        console.error('Error', error)
        if (error.message == '422 Unprocessable Entity. Validation failed.') {
          const responseErrors = error.errors || {};
          const { formattedErrors, friendlyMessages } = buildValidationErrorPayload(responseErrors);

          setErrors(formattedErrors);

          toast.warning('Validation Error.', {
            description: friendlyMessages[0] || 'Please check your input. Some fields may be invalid.',
            action: {
              label: 'Ok',
            },
          });
          return
        }
        if (error.message.includes(`Can't update status:`)) {
          toast.warning('Cannot deactivate this contact', {
            description: 'This contact is linked to one or more open/pending matters and cannot be deactivated until those matters are closed.',
          });
          return
        }
        toast.error('Oops! Something went wrong.', {
          description: 'An error occurred while updating Contact. Please try again in a moment.',
        });
      } finally {
        setCreateContactLoading(false);
      }
    } else {
      setCreateContactLoading(true);
      try {
        const { data, status } = await contactService.createContact(formData);
        if (status) {
          resetForm()
          toast.success('Contact Created Successfully.', {
            description: `${data.first_name + ' ' + data.last_name} Contact Created Successfully.`,
            action: {
              label: 'Ok',
            },
          });
          Navigate('/contacts')
        }
      } catch (error) {
        console.error('Error', error)
        // if(error.message == '422 Unprocessable Entity. Validation failed.') {
        //   // Validation for numbers
        //   const responseErrors = error.errors || {};
        //   const formattedErrors = {};

        //   Object.entries(responseErrors).forEach(([key, value]) => {
        //     if (key.startsWith('phone_number')) {
        //       // Extract index and nested field from e.g. "phone_number.0.number"
        //       const match = key.match(/^phone_number\.(\d+)\.(\w+)$/);
        //       if (match) {
        //         const [, index, field] = match;

        //         if (!formattedErrors.phone_number) {
        //           formattedErrors.phone_number = [];
        //         }

        //         formattedErrors.phone_number[Number(index)] = {
        //           ...(formattedErrors.phone_number[Number(index)] || {}),
        //           [field]: value[0], // or join with \n if multiple messages
        //         };
        //       }
        //     } else {
        //       formattedErrors[key] = value[0]; // single error message
        //     }
        //   });
        //   // console.log(formattedErrors)
        //   setErrors(formattedErrors)
        //   if(responseErrors["phone_number.0.number"] || responseErrors["phone_number.1.number"] || responseErrors["phone_number.2.number"]) {
        //     toast.warning('Mobile Number Already Taken.', {
        //       description: 'Please check you Mobile Numbers. it has has already been taken.',
        //       action: {
        //         label: 'Ok',
        //       },
        //     });
        //     return
        //   }
        //   toast.warning('Validation Error.', {
        //     description: 'Please check your input. Some fields may be invalid.',
        //     action: {
        //       label: 'Ok',
        //     },
        //   });
        //   return
        // } 
        if (error.message === '422 Unprocessable Entity. Validation failed.') {
          const responseErrors = error.errors || {};
          const { formattedErrors, friendlyMessages } = buildValidationErrorPayload(responseErrors);

          setErrors(formattedErrors);

          // A duplicate email points at an existing contact — let the user jump to it.
          const duplicate = error.duplicate_contact;
          if (duplicate?.id) {
            toast.warning('Contact already exists', {
              description: `A contact named "${duplicate.name}" already uses this email.`,
              action: {
                label: 'View contact',
                onClick: () => Navigate(`/contacts/${duplicate.id}`),
              },
            });
            return;
          }

          toast.warning('Validation Error.', {
            description: friendlyMessages[0] || 'Please check your input. Some fields may be invalid.',
            action: {
              label: 'Ok',
            },
          });
          return;
        }
        const fallbackMessage = error?.detail || error?.message || 'An error occurred while creating Contact. Please try again in a moment.';
        toast.error('Oops! Something went wrong.', {
          description: fallbackMessage,

        });
      } finally {
        setCreateContactLoading(false);
      }
    }
  };

  const getSingleContact = async (contactId) => {
    setDataLoading(true)
    try {
      const { data, status } = await contactService.getSingleContact(contactId);
      if (status) {
        setContactData(data)
      }
    } catch (error) {
      console.error('Error', error)
      toast.error('Failed to fetch Contact.', {
        description: 'An error occurred while fetching contact. Please try again later.',

      });
    } finally {
      setDataLoading(false)
    }
  }

  const getAllTemplates = async () => {
    try {
      const { data, status } = await templateService.getAllInvoiceTemplate();
      if (status) {
        const Data = data.map(template => ({
          label: template.name,
          value: template.id.toString(),
          id: template.id.toString(),
        }));
        setTemplateData(Data)
        return Data
      }
    } catch (error) {
      console.error('Error', error)
      toast.error('Failed to fetch Templates.', {
        description: 'An error occurred while fetching templates. Please try again later.',

      });
    }
  }
  const {
    data: templateOptions,
    loading: templateLoading,
    load: loadTemplates
  } = useLazyOptions(getAllTemplates);

  const handleModalClose = () => {
    setIsModalOpen(false)
  }

  const handleSubmitCustomField = async (formData, formikHelpers) => {
    setIsLoading(true)
    try {
      const { data, status } = await customFieldService.createCustomField(formData);
      if (status) {
        setIsModalOpen(false)
        getAllCustomFields()
        toast.success('Custom Field Created Successfully.', {
          description: `${data.title} field for ${data.type} Created Successfully.`,

        });
      }
    } catch (error) {
      console.error('Error', error)
      if (error.message == '422 Unprocessable Entity. Validation failed.') {
        const validationErrors = error.errors
        const formattedErrors = Object.fromEntries(
          Object.entries(validationErrors).map(([key, value]) => [key, value[0]])
        );

        formikHelpers.setErrors(formattedErrors);
        toast.error('Validation Error.', {
          description: 'Please check your input. Some fields may be invalid.',

        });
      }
    } finally {
      setIsLoading(false)
    }
  };

  const handleCloseContactType = () => {
    // setItemId(null)
    setSelectedData(null)
    setMode('create')
    setOpen(false)
  }

  const handleSubmitContactType = async (formData) => {
    setIsLoading(true)
    try {
      const { data, status } = await contactTypeService.createContactType(formData);
      if (status) {
        // resetTypes()
        setOpen(false)
        // getAllContactType()
        toast.success('Contact Type Created Successfully.', {
          description: `${data.name} Contact Type Created Successfully.`,

        });
      }
    } catch (error) {
      console.error('Error', error)
      if (error.message == '422 Unprocessable Entity. Validation failed.') {
        toast.warning('Validation Error.', {
          description: 'Please check your input. Some fields may be invalid.',

        });
        return
      }
      toast.error('Failed to Create Contact Type', {
        description: 'An error occurred while creating contact type. Please try again later.',

      });
    } finally {
      setIsLoading(false)
    }
  };


  useEffect(() => {
    getAllCustomFields();
    if (contactId) { getSingleContact(contactId) }
    else { setContactData({}) }
  }, [contactId])


  useEffect(() => {
    if (!customFields?.length) return;

    const defaultVisible = customFields
      .filter(f => f.type === 'contact')
      .filter(f =>
        f.is_show === 1 ||
        contactData?.custom_fields?.some(cf => cf.id === f.id && cf.pivot?.value)
      )
      .map(f => f.id);

    setVisibleCustomFields(prev => {
      const validIds = new Set(customFields.filter(f => f.type === 'contact').map(f => f.id));
      const merged = new Set([...defaultVisible, ...prev].filter(id => validIds.has(id)));
      return Array.from(merged);
    });
  }, [customFields, contactData, contactId]);



  // const useContactInitialValues = (contactData, customFields) => {
  //   return useMemo(() => {
  //     if (!contactData) return {};

  //     // Handle custom fields
  //     const customFieldValues = {};
  //     customFields?.forEach((field) => {
  //       if (field.type !== 'contact') return;

  //       const match = contactData.custom_fields?.find(f => f.id === field.id);
  //       let value = match?.pivot?.value ?? '';
  //       console.log('Match val', match?.pivot?.value)

  //       if (field.field_type === 'dropdown' && value) {
  //         value = value.toLowerCase();
  //       }

  //       if (field.is_multi_select === 1) {
  //         if (Array.isArray(value)) {
  //           // already correct
  //         }
  //         else if (typeof value === 'string' && value.length) {
  //           try {
  //             const parsed = JSON.parse(value);
  //             value = Array.isArray(parsed) ? parsed : [];
  //           } catch {
  //             value = value.split(',').map(v => v.trim());
  //           }
  //         } else {
  //           value = [];
  //         }
  //       }

  //       // checkbox handling
  //       if (field.field_type === 'checkbox') {
  //         console.log('Value in checkbox', value)
  //         value = value === '1' || value === "1" || value === 1 || value === true;
  //       }

  //       if (field.is_show === 1 || !!match?.pivot) {
  //         customFieldValues[field.id] = value;
  //       }
  //     });;
  //     console.log('customFieldValues', customFieldValues)
  //     // Core data mapping
  //     const address = contactData.address?.[0] || {};
  //     const tags = contactData.tags || [];
  //     const assigned = contactData.assigned_to || [];

  //     return {
  //       business_name: contactData.business_name || '',
  //       first_name: contactData.first_name || '',
  //       middle_name: contactData.middle_name || '',
  //       last_name: contactData.last_name || '',
  //       email: contactData.email || '',
  //       add_type: address.add_type || '',
  //       address: address.address || '',
  //       address2: address.address2 || '',
  //       city: address.city || '',
  //       state: address.state || '',
  //       zip: address.zip || '',
  //       office_number: contactData.office_number || '',
  //       home_number: contactData.home_number || '',
  //       fax_number: contactData.fax_number || '',
  //       tags: tags.map(tag => tag.id),
  //       tags_arr: tags.map(tag => ({
  //         label: tag.name,
  //         value: tag.id,
  //         id: tag.id,
  //       })),
  //       assigned_to: assigned.map(user => user.id),
  //       assigned_to_user: assigned.map(user => ({
  //         label: `${user.first_name} ${user.last_name}`,
  //         value: user.id,
  //         id: user.id,
  //         email: user.email,
  //       })),
  //       status: contactData.status || 'active',
  //       website: contactData.website || '',
  //       invoice_template_id: contactData.invoice_template_uuid || '',
  //       invoice_template: contactData.invoice_template_uuid
  //         ? {
  //           label: contactData.invoice_template.name,
  //           value: contactData.invoice_template.id,
  //           id: contactData.invoice_template.id,
  //         }
  //         : null,
  //       contact_type_id: contactData?.contact_type?.id || '',
  //       contact_type: contactData.contact_type
  //         ? {
  //           label: contactData.contact_type.name,
  //           value: contactData.contact_type.id,
  //           id: contactData.contact_type.id,
  //         }
  //         : null,
  //       note: contactData.note || '',
  //       allow_secure_messaging: contactData.allow_secure_messaging === 1,
  //       allow_file_upload: contactData.allow_file_upload === 1,
  //       mobile_numbers:
  //         contactData.phones?.length > 0
  //           ? contactData.phones.map((phone) => ({
  //             title: phone.title || '',
  //             number: phone.number || '',
  //           }))
  //           : [{ title: '', number: '' }],
  //       speciality: contactData.speciality || '',
  //       custom_fields: customFieldValues,
  //       dob: contactData.dob || null,
  //       dol: contactData.dol || null,
  //     };
  //   }, [contactData, customFields]);
  // };

  const useContactInitialValues = (contactData, customFields) => {
    return useMemo(() => {
      if (!contactData) return {};

      // Handle custom fields
      const customFieldValues = {};
      customFields?.forEach((field) => {
        if (field.type !== 'contact') return;

        const match = contactData.custom_fields?.find(f => f.id === field.id);
        let value = match?.pivot?.value ?? '';
        console.log('Match val', match?.pivot?.value);

        // Handle value for dropdown fields (make sure it's lowercase)
        if (field.field_type === 'dropdown' && value) {
          value = value.toLowerCase();
        }

        // Handle multi-select fields (for checkboxes and dropdowns)
        if (field.is_multi_select === 1) {
          if (Array.isArray(value)) {
            // Already an array, no further processing needed
          } else if (typeof value === 'string' && value.length) {
            try {
              const parsed = JSON.parse(value); // Try to parse it as an array
              value = Array.isArray(parsed) ? parsed : [];
            } catch {
              value = value.split(',').map(v => v.trim());
            }
          } else {
            value = [];
          }
        }

        // Checkbox handling (single checkbox)
        if (field.field_type === 'checkbox' && field.is_multi_select === 0) {
          console.log('Value in checkbox', value);

          if (typeof value === 'string') {
            const trimmedValue = value.trim();
            if (!trimmedValue || trimmedValue === '0' || trimmedValue.toLowerCase() === 'false') {
              value = false;
            } else if (trimmedValue === '1' || trimmedValue.toLowerCase() === 'true') {
              value = true;
            } else {
              try {
                const parsedValue = JSON.parse(trimmedValue);
                if (parsedValue && typeof parsedValue === 'object' && !Array.isArray(parsedValue)) {
                  value = {
                    checked: Boolean(parsedValue.checked),
                    other: typeof parsedValue.other === 'string' ? parsedValue.other : '',
                  };
                } else {
                  value = trimmedValue;
                }
              } catch {
                value = trimmedValue;
              }
            }
          } else {
            value = value === 1 || value === true;
          }
        }

        // Handle multi-checkbox fields (where is_multi_select is 1)
        if (field.field_type === 'checkbox' && field.is_multi_select === 1) {
          if (typeof value === 'string') {
            // If the value is a string like '["yes", "no", "what"]', parse it
            try {
              value = JSON.parse(value);
            } catch {
              value = [];
            }
          }

          if (!Array.isArray(value)) {
            value = [];
          }

          // Ensure all values are in lowercase for comparison
          value = value.map(v => v.toLowerCase());
        }

        // Assign value to customFieldValues if the field is showable
        if (field.is_show === 1 || !!match?.pivot) {
          customFieldValues[field.id] = value;
        }
      });

      console.log('customFieldValues', customFieldValues);

      // Core data mapping
      const address = contactData.address?.[0] || {};
      const tags = contactData.tags || [];
      const assigned = contactData.assigned_to || [];

      return {
        business_name: contactData.business_name || '',
        first_name: contactData.first_name || '',
        middle_name: contactData.middle_name || '',
        last_name: contactData.last_name || '',
        email: contactData.email || '',
        add_type: address.add_type || '',
        address: address.address || '',
        address2: address.address2 || '',
        city: address.city || '',
        state: address.state || '',
        zip: address.zip || '',
        office_number: contactData.office_number || '',
        home_number: contactData.home_number || '',
        fax_number: contactData.fax_number || '',
        tags: tags.map(tag => tag.id),
        tags_arr: tags.map(tag => ({
          label: tag.name,
          value: tag.id,
          id: tag.id,
        })),
        assigned_to: assigned.map(user => user.id),
        assigned_to_user: assigned.map(user => ({
          label: `${user.first_name} ${user.last_name}`,
          value: user.id,
          id: user.id,
          email: user.email,
        })),
        status: contactData.status || 'active',
        website: contactData.website || '',
        invoice_template_id: contactData.invoice_template?.id || '',
        invoice_template: contactData.invoice_template
          ? {
            label: contactData.invoice_template.name,
            value: contactData.invoice_template.id,
            id: contactData.invoice_template.id,
          }
          : null,
        contact_type_id: contactData?.contact_type?.id || '',
        contact_type: contactData.contact_type
          ? {
            label: contactData.contact_type.name,
            value: contactData.contact_type.id,
            id: contactData.contact_type.id,
          }
          : null,
        note: contactData.note || '',
        allow_secure_messaging: contactData.allow_secure_messaging === 1,
        allow_file_upload: contactData.allow_file_upload === 1,
        mobile_numbers:
          contactData.phones?.length > 0
            ? contactData.phones.map((phone) => ({
              title: phone.title || '',
              number: phone.number || '',
            }))
            : [{ title: '', number: '' }],
        speciality: contactData.speciality || '',
        custom_fields: customFieldValues,
        dob: contactData.dob || null,
        dol: contactData.dol || null,
      };
    }, [contactData, customFields]);
  };


  const initialValues = useContactInitialValues(contactData, customFields);

  useEffect(() => {
    if (!formikRef.current || !Object.keys(initialValues).length) return;
    formikRef.current.setValues(initialValues);
  }, [initialValues]);



  const StateCitySelect = () => {
    const { values, setFieldValue } = useFormikContext();

    const stateOptions = Object.keys(stateCityMap).map(state => ({
      label: state,
      value: state,
    }));

    const cityOptions = values.state
      ? stateCityMap[values.state]?.map(city => ({
        label: city,
        value: city,
      })) || []
      : [];

    const handleStateChange = (option) => {
      setFieldValue('state', option?.value || '');
      setFieldValue('city', ''); // reset city
    };

    const handleCityChange = (option) => {
      setFieldValue('city', option?.value || '');

      if (!option) return;

      const foundState = Object.keys(stateCityMap).find(state =>
        stateCityMap[state].includes(option.value)
      );

      if (foundState) {
        setFieldValue('state', foundState);
      }
    };

    return (
      <>
        {/* STATE */}
        <div>
          <label className="mb-1 form-label">State</label>
          <LazyFormikSelect
            name="state"
            options={stateOptions}
            placeholder="Select State"
            onOptionSelect={handleStateChange}
            preloadedOption={
              values.state
                ? { label: values.state, value: values.state }
                : null
            }
          />
          <ErrorMessage name="state" component="div" className="text-red-500 text-[11px] mt-1" />
        </div>
        {/* CITY */}
        <div>
          <label className="mb-1 form-label">City</label>
          <LazyFormikSelect
            name="city"
            options={cityOptions}
            placeholder="Select City"
            onOptionSelect={handleCityChange}
            isDisabled={!values.state && !values.city}
            preloadedOption={
              values.city
                ? { label: values.city, value: values.city }
                : null
            }
          />
          <ErrorMessage name="city" component="div" className="text-red-500 text-[11px] mt-1" />
        </div>
      </>
    );
  };

  return (
    <>
      <Formik
        innerRef={formikRef}
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={getValidationSchema(customFields)}
        validateOnBlur={true}
        validateOnChange={true}
        validateOnMount={true}
        context={{ mobile_numbers: initialValues?.mobile_numbers }}
        onSubmit={(values, { resetForm, setErrors }) => {
          const tags = MapIdsToLabels(values.tags, initialValues?.tags_arr);

          const phoneNumbers = values.mobile_numbers.map((entry) => ({
            title: entry.title,
            number: entry.number,
          }));

          const address = [
            {
              add_type: values.add_type,
              address: values.address,
              address2: values.address2,
              city: values.city || null,
              state: values.state,
              zip: values.zip,
            },
          ];

          const filteredCustomFields = Object.entries(values.custom_fields || {})
            .filter(([key, value]) =>
              visibleCustomFields.includes(key) && value !== '' && value !== null && value !== undefined
            ).reduce((acc, [key, value]) => {
              if (value && typeof value === 'object' && !Array.isArray(value)) {
                acc[key] = JSON.stringify({
                  checked: Boolean(value.checked),
                  other: typeof value.other === 'string' ? value.other : '',
                });
              } else {
                acc[key] = typeof value === 'boolean' ? (value ? '1' : '0') : value;
              }
              return acc;
            }, {});

          const {
            mobile_numbers,
            add_type,
            address: addr,
            address2,
            city,
            state,
            zip,
            custom_fields,
            ...rest
          } = values;

          const payload = {
            ...rest,
            phone_number: phoneNumbers,
            address,
            tags,
            custom_fields: filteredCustomFields,
          };
          // return console.log("Form Submitted:", payload, values); 
          handleSubmit(payload, resetForm, setErrors);
        }}
      >
        {({ errors, values, handleSubmit, setFieldValue, setValues, setTouched, setErrors, setFieldTouched }) => {
          console.error('Errors', errors)
          useEffect(() => {
            const timeout = setTimeout(() => {
              setValues(prev => ({
                ...initialValues,
                ...formValues,
                custom_fields: {
                  ...prev.custom_fields,
                  ...formValues.custom_fields,
                }
              }));
            }, 0);
            return () => clearTimeout(timeout);
          }, [customFields]); // Only runs when fields change

          useEffect(() => {
            // Sync latest values into external state
            setFormValues(values);
          }, [values]);
          return (
            <Form onSubmit={handleSubmit} className='px-2.5'>
              <FormikUnsavedChangesGuard saving={createContactLoading} saveLabel="Save contact" />
              <fieldset disabled={dataLoading}>
                <div className="card pb-2.5">
                  <div className="card-header">
                    <h3 className="card-title">{contactId ? 'Edit Contact' : 'Create Contact'}</h3>

                  </div>
                  <div className="grid gap-5 card-body">
                    <div className='flex items-center'>
                      <span className='w-12 h-0.5 bg-border inline-block mr-2'></span>
                      <p className='text-sm'>Personal Information</p>
                    </div>
                    {/* Personal Info */}
                    <div className="grid gap-5 sm:grid-cols-4 xs:grid-cols-2">
                      <div>
                        <label className="mb-1 form-label">Contact Type <span className='pl-px text-red-600'>*</span></label>
                        <LazyFormikSelect
                          name='contact_type_id'
                          placeholder="Select Contact Type"
                          loadOptions={loadContactTypeOptions}
                          pageSize={20}
                          onOptionSelect={(selected) => {
                            if (selected?.id) {
                              setFieldValue('contact_type_id', selected.value).then(() => {
                                setFieldTouched('contact_type_id', true, true); // force validation + clear error
                              });
                              setFieldValue('contact_type', selected.label);
                              if (selected.label !== "Provider") {
                                setFieldValue("allow_file_upload", false);
                              }
                            }
                          }}
                          noOptionsMessage={() => (
                            isLoading ? (
                              <span className="text-sm">Loading...</span>
                            ) : (
                              <div>
                                <span className="mr-1 text-sm">No options available</span>
                                <Can I='handle' a='contact' ability={ability}>
                                  <button type="button"
                                    onClick={() => setOpen(true)}
                                    className="text-sm text-blue-500 underline"
                                  >
                                    Add new
                                  </button>
                                </Can>
                              </div>
                            )
                          )}
                          preloadedOption={initialValues.contact_type}
                        />
                        <ErrorMessage name="contact_type_id" component="div" className="text-red-500 text-[11px] mt-1" />
                      </div>
                      <div>
                        <label className="mb-1 form-label">First Name <span className='pl-px text-red-600'>*</span></label>
                        <Field className="input" type="text" name="first_name" />
                        <ErrorMessage name="first_name" component="div" className="text-red-500 text-[11px] mt-1" />
                      </div>
                      <div>
                        <label className="mb-1 form-label">Middle Name </label>
                        <Field className="input" type="text" name="middle_name" />
                      </div>
                      <div>
                        <label className="mb-1 form-label">Last Name <span className='pl-px text-red-600'>*</span></label>
                        <Field className="input" type="text" name="last_name" />
                        <ErrorMessage name="last_name" component="div" className="text-red-500 text-[11px] mt-1" />
                      </div>
                    </div>

                    <div className="grid gap-5 sm:grid-cols-4 xs:grid-cols-2">
                      <div>
                        <label className="mb-1 form-label">Business Name </label>
                        <Field className="input" type="text" name="business_name" />
                        <ErrorMessage name="business_name" component="div" className="text-red-500 text-[11px] mt-1" />
                      </div>
                      <div>
                        <label className="mb-1 form-label">Email </label>
                        <Field className="input" type="email" name="email" />
                        <ErrorMessage name="email" component="div" className="text-red-500 text-[11px] mt-1" />
                      </div>
                      <div>
                        <label className="mb-1 form-label">Office Number</label>
                        <MaskedInput className="input no-spinner" name="office_number" />
                      </div>
                      <div>
                        <label className="mb-1 form-label">Home Number</label>
                        <MaskedInput className="input no-spinner" name="home_number" />
                      </div>
                      <div>
                        <label className="mb-1 form-label">Fax Number</label>
                        <MaskedInput className="input no-spinner" name="fax_number" />
                      </div>
                    </div>

                    <div className='flex items-center mt-8'>
                      <span className='w-12 h-0.5 bg-border inline-block mr-2'></span>
                      <p className='text-sm'>Phone Number</p>
                    </div>
                    <div className=''>
                      <FieldArray name="mobile_numbers">
                        {({ push, remove, form }) => (
                          <>
                            <div className='grid gap-5 sm:grid-cols-2 xs:grid-cols-2'>
                              {form.values.mobile_numbers.map((_, index) => (
                                <div key={index} className="flex items-start gap-2">
                                  <div>
                                    <label className="mb-1 form-label">Type</label>
                                    <Select
                                      inputId={`mobile_numbers-${index}-title`}
                                      options={phoneTypeOptions(form.values.mobile_numbers?.[index]?.title)}
                                      value={
                                        form.values.mobile_numbers?.[index]?.title
                                          ? {
                                            label: form.values.mobile_numbers[index].title,
                                            value: form.values.mobile_numbers[index].title,
                                          }
                                          : null
                                      }
                                      onChange={(selected) =>
                                        form.setFieldValue(
                                          `mobile_numbers[${index}].title`,
                                          selected?.value ?? ''
                                        )
                                      }
                                      isClearable
                                      placeholder="Select"
                                      classNames={getGlobalSelectClass()}
                                      className="p-0 input react-select-container"
                                      classNamePrefix="react-select"
                                    />
                                    <ErrorMessage
                                      name={`mobile_numbers[${index}].title`}
                                      component="div"
                                      className="text-red-500 text-[11px] mt-1"
                                    />
                                  </div>
                                  <div>
                                    <label className="mb-1 form-label">Number </label>
                                    <MaskedInput className="input no-spinner" name={`mobile_numbers[${index}].number`} />
                                  </div>
                                  <div>
                                    <span className='text-transparent bg-transparent select-none'>.</span>
                                    <button
                                      type="button"
                                      className="flex items-center justify-center btn btn-sm btn-danger btn-outline"
                                      onClick={() => remove(index)}
                                      disabled={form.values.mobile_numbers.length === 1} // Prevent removing the last field
                                    >
                                      <KeenIcon icon="trash" className="text-white" />
                                    </button>

                                  </div>
                                </div>
                              ))}
                            </div>
                            {form.values.mobile_numbers.length < 3 && (
                              <>
                                <button
                                  type="button"
                                  className="mt-4 w-fit btn btn-link btn-sm"
                                  onClick={() => push({ title: '', number: '' })} // Add a new empty field
                                >
                                  Add Phone Number
                                </button>
                              </>
                            )}
                          </>
                        )}
                      </FieldArray>
                    </div>

                    <div className='flex items-center mt-8'>
                      <span className='w-12 h-0.5 bg-border inline-block mr-2'></span>
                      <p className='text-sm'>Address Information</p>
                    </div>
                    {/* Address Info */}
                    <div className="grid gap-5 xs:grid-cols-3">
                      <div>
                        <label className="mb-1 form-label">Address Type </label>
                        <Field className="input" type="text" name="add_type" placeholder='e.g., Office, Home' />
                        <ErrorMessage name="add_type" component="div" className="text-red-500 text-[11px] mt-1" />
                      </div>
                      <div>
                        <label className="mb-1 form-label">Address </label>
                        <Field className="input" type="text" name="address" />
                        <ErrorMessage name="address" component="div" className="text-red-500 text-[11px] mt-1" />
                      </div>
                      <div>
                        <label className="mb-1 form-label">Address 2</label>
                        <Field className="input" type="text" name="address2" />
                        <ErrorMessage name="address2" component="div" className="text-red-500 text-[11px] mt-1" />
                      </div>
                      <div>
                        <label className="mb-1 form-label">City</label>
                        <Field className="input" type="text" name="city" />
                        <ErrorMessage name="city" component="div" className="text-red-500 text-[11px] mt-1" />
                      </div>
                      <FormikStateSelect />
                      {/*<div>
                        <label className="mb-1 form-label">State </label>
                        <Field className="input" type="text" name="state" />
                        <ErrorMessage name="state" component="div" className="text-red-500 text-[11px] mt-1" />
                      </div> */}
                      <div>
                        <label className="mb-1 form-label">Zip </label>
                        <Field className="input" type="text" name="zip" />
                        <ErrorMessage name="zip" component="div" className="text-red-500 text-[11px] mt-1" />
                      </div>
                    </div>


                    <div className='flex items-center mt-8'>
                      <span className='w-12 h-0.5 bg-border inline-block mr-2'></span>
                      <p className='text-sm'>Additional Information</p>
                    </div>
                    {/* Row 4 */}
                    <div className="grid gap-5 xs:grid-cols-2">
                      <div className='w-full'>
                        <label className="mb-1 form-label">Tags</label>
                        <LazyFormikSelect
                          name='tags'
                          placeholder="Select Tags"
                          loadOptions={loadTagOptions}
                          pageSize={20}
                          isMulti={true}
                          creatable={true}
                          preloadedOption={initialValues.tags_arr.length > 0 ? initialValues.tags_arr : []}
                        />
                        <ErrorMessage name="tags" component="div" className="text-red-500 text-[11px] mt-1" />
                      </div>
                      <div className='w-full'>
                        <label className="mb-1 form-label">Assigned To </label>
                        <LazyFormikSelect
                          name='assigned_to'
                          loadOptions={loadUserOptions}
                          pageSize={20}
                          placeholder="Select Assignee"
                          // onFocus={() => loadUser(null)}
                          // isLoading={userLoading}
                          isMulti={true}
                          preloadedOption={initialValues.assigned_to_user.length > 0 ? initialValues.assigned_to_user : []}
                          components={{
                            Option: CustomOption,
                            SingleValue: CustomSingleValue,
                          }}
                        />
                        <ErrorMessage name="assigned_to" component="div" className="text-red-500 text-[11px] mt-1" />
                      </div>
                    </div>

                    {/* Row 5 */}
                    <div className="grid grid-cols-2 gap-5">
                      <div>
                        <label className="mb-1 form-label">Website</label>
                        <Field className="input" type="text" name="website" />
                        <ErrorMessage name="website" component="div" className="text-red-500 text-[11px] mt-1" />
                      </div>
                      <div>
                        <label className="mb-1 form-label">Status</label>
                        <LazyFormikSelect
                          name='status'
                          options={[
                            { value: 'active', label: 'Active' },
                            { value: 'inactive', label: 'Inactive' },
                          ]}
                          placeholder="Select Status"
                        />
                        <ErrorMessage name="status" component="div" className="text-red-500 text-[11px] mt-1" />
                        {/* <Field as="select" className="input" name="status">
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </Field> */}
                      </div>
                    </div>

                    {/* Row 6 */}
                    <div className="grid gap-5 xs:grid-cols-2">
                      <div>
                        <label className="mb-1 form-label">Invoice Template <span className='pl-px text-red-600'>*</span></label>
                        <LazyFormikSelect
                          name={`invoice_template_id`}
                          options={templateOptions}
                          placeholder="Select a Template"
                          onFocus={() => loadTemplates(null)}
                          isLoading={templateLoading}
                          preloadedOption={initialValues.invoice_template}
                        />
                        <ErrorMessage name="invoice_template_id" component="div" className="text-red-500 text-[11px] mt-1" />
                        {/* <Field as="select" className="input" name="invoice_template_id">
                        <option value="">Select Invoice Template</option>
                        <option value="">Template 1</option>
                        <option value="">Template 2</option>
                      </Field> */}
                      </div>
                      <div>
                        <label className="mb-1 form-label">Specialty </label>
                        <LazyFormikSelect
                          name='speciality'
                          placeholder="Select Specialty"
                          loadOptions={loadSpecialityOptions}
                          preloadedOption={initialValues.speciality ? { label: initialValues.speciality, value: initialValues.speciality } : null}
                        />
                        <ErrorMessage name="speciality" component="div" className="text-red-500 text-[11px] mt-1" />
                      </div>
                      <div>
                        <label className="mb-1 form-label">Date Of Birth</label>
                        <Field name="dob" component={DatePicker} isDob isRange={false} />
                        <ErrorMessage name="dob" component="div" className="mt-1 text-xs text-red-500" />
                      </div>
                      <div>
                        <label className="mb-1 form-label">Date Of Loss</label>
                        <Field name="dol" component={DatePicker} disableFuture={true} isRange={false} />
                        <ErrorMessage name="dol" component="div" className="mt-1 text-xs text-red-500" />
                      </div>
                    </div>

                    {customFields.filter(f => f.type === 'contact') && (
                      <div className='flex items-center mt-8'>
                        <span className='w-12 h-0.5 bg-border inline-block mr-2'></span>
                        <p className='text-sm'>Custom Fields</p>
                      </div>
                    )}
                    <div className="grid gap-5 xs:grid-cols-2">
                      {customFields
                        .filter(f => f.type === 'contact' && visibleCustomFields.includes(f.id))
                        .map(field =>
                          renderCustomField(
                            field,
                            { setFieldValue, values, errors },
                            () => {
                              // remove field
                              setVisibleCustomFields(prev => prev.filter(id => id !== field.id));
                              setFieldValue(`custom_fields.${field.id}`, '', false);
                              setTouched(prev => {
                                const updated = { ...prev };
                                if (updated.custom_fields) {
                                  updated.custom_fields = { ...updated.custom_fields };
                                  delete updated.custom_fields[field.id];
                                }
                                return updated;
                              });
                              setErrors(prev => {
                                const updated = { ...prev };
                                if (updated.custom_fields) {
                                  updated.custom_fields = { ...updated.custom_fields };
                                  delete updated.custom_fields[field.id];
                                }
                                return updated;
                              });
                            }
                          )
                        )}

                      <div>
                        {/* && !visibleCustomFields.includes(f.id)  .length > 0 */}
                        <label className="mb-1 form-label">Add Custom Field</label>
                        <Select
                          value={addFieldSelect}
                          options={customFields
                            .filter(f => f.type === 'contact' && !visibleCustomFields.includes(f.id))
                            .map(f => ({ label: f.title, value: f.id }))}
                          onChange={selected => {
                            if (selected?.value) {
                              setVisibleCustomFields(prev => [...prev, selected.value]);
                              if (!values.custom_fields?.[selected.value]) {
                                setFieldValue(
                                  `custom_fields.${selected.value}`,
                                  customFields.find(f => f.id === selected.value)?.is_multi_select === 1
                                    ? []
                                    : ''
                                );
                              }
                            }
                            setAddFieldSelect(null);
                          }}
                          placeholder="Choose a custom field to add"
                          classNames={getGlobalSelectClass()}
                          className="p-0 input react-select-container"
                          classNamePrefix="react-select"
                          noOptionsMessage={() => (
                            isLoading ? (
                              <span className="text-sm">Loading...</span>
                            ) : (
                              <div>
                                <span className="mr-1 text-sm">No options available</span>
                                <button type="button"
                                  onClick={() => setIsModalOpen(true)}
                                  className="text-sm text-blue-500 underline"
                                >
                                  Add new
                                </button>
                              </div>
                            )
                          )}
                        />
                        {/* {customFields.filter(f => f.type === 'contact') && (
                        <>
                        </>
                      )} */}
                      </div>
                    </div>

                    <div className='flex items-center mt-8'>
                      <span className='w-12 h-0.5 bg-border inline-block mr-2'></span>
                      <p className='text-sm'>Additional Message</p>
                    </div>
                    <div>
                      <label className="mb-1 form-label">Note</label>
                      <Field as="textarea" className="input pt-2 h-[120px]" name="note" />
                    </div>

                    <div className='flex flex-wrap items-center gap-4'>
                      <Field name="allow_secure_messaging">
                        {({ field, form }) => (
                          <label className="switch w-fit">
                            <span className="switch-label">Allow Secure Messaging</span>
                            <input
                              type="checkbox"
                              checked={field.value}
                              onChange={() => form.setFieldValue("allow_secure_messaging", !field.value)}
                            />
                          </label>
                        )}
                      </Field>
                      <Field name="allow_file_upload">
                        {({ field, form }) => (
                          <label className="switch w-fit">
                            <span className="switch-label">Allow File Upload </span>
                            <input
                              type="checkbox"
                              disabled={
                                !(
                                  values.contact_type === "Provider" ||
                                  values.contact_type?.label === "Provider"
                                )
                              }
                              checked={field.value}
                              onChange={() =>
                                form.setFieldValue("allow_file_upload", !field.value)
                              }
                            />
                          </label>
                        )}
                      </Field>

                      {/* {values.contact_type === 'Provider' && (
                      )} */}
                    </div>

                    {/* Submit Button */}
                    <div className="inline-flex items-center justify-end gap-2">
                      {/* <button type="button" disabled={isLoading || createContactLoading} onClick={() => setIsModalOpen(true)} className="btn btn-link btn-sm h-fit">
                      Create Custom Field
                    </button> */}
                      <button type="submit" disabled={createContactLoading} className="btn btn-primary">
                        {contactId ?
                          createContactLoading ? 'Please Wait...' : 'Update Contact' :
                          createContactLoading ? 'Please Wait...' : 'Create Contact'
                        }
                      </button>
                    </div>

                  </div>
                </div>
              </fieldset>
            </Form>
          )
        }}
      </Formik>
      <CustomFieldModal
        open={isModalOpen}
        onClose={handleModalClose}
        mode={'create'}
        data={null}
        onSubmit={handleSubmitCustomField}
        createFieldLoading={isLoading}
        updateFieldLoading={updateFieldloading}
        moduleType="contact"
        ref={modalFormikRef}
      />
      <ContactTypeModal
        open={open}
        onCloseModal={handleCloseContactType}
        handleMode={mode}
        data={selectedData}
        onSubmit={handleSubmitContactType}
        createFieldLoading={isLoading}
        updateFieldLoading={loading}
      />
    </>
  );
};
export { AddContacts };
